import { Crown } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const procenaNarcisoidnosti: QuizConfig = {
  id: "procena-narcisoidnosti",
  illustration: {
    src: "/images/quizzes/procena-narcisoidnosti.webp",
    alt: "Muškarac govori za stolom dok druga osoba čeka priliku da se uključi.",
  },
  lastModified: "2026-10-05",
  title: "Test narcisoidnosti (besplatan) | Da li si narcis?",
  shortTitle: "Procena narcisoidnosti",
  description:
    "Saznaj da li ispoljavaš narcisoidne osobine i kako utiču na tvoje odnose. Online test sa personalizovanom analizom, rezultati odmah. Traje 5-7 min.",
  longDescription:
    "Subjektivan test koji meri koliko često stavljaš sebe ispred drugih i kako to utiče na odnose.",
  icon: Crown,
  duration: "5-7 min",
  category: "Ličnost i emocije",
  focusAreasWanted: [
    "relationships",
    "self_development",
    "self_esteem",
    "emotional_regulation",
  ],
  questions: [
    {
      type: "radio",
      question: "Pol:",
      options: ["Muški", "Ženski", "Ne želim da se izjasnim"],
      scores: [0, 0, 0],
    },
    {
      type: "radio",
      question: "Kako bi opisao/la svoje samopouzdanje?",
      options: [
        "Nisko",
        "Promenljivo, zavisi od situacije",
        "Umereno visoko",
        "Vrlo visoko",
        "Ekstremno visoko, osećam se superiorno u odnosu na druge",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Da li osećaš da si stvoren/a za velike stvari?",
      options: [
        "Ne, svi smo jednaki",
        "Voleo/la bih, ali nisam siguran/na",
        "Mislim da imam posebne talente",
        "Siguran/na sam da zaslužujem više od drugih",
        "Zaslužujem izuzetan status, ali me ljudi ne prepoznaju",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Šta ti je najvažnije u interakciji sa ljudima? (označi sve što važi)",
      options: [
        "Da dobijem priznanje i divljenje",
        "Da budem centar pažnje",
        "Da imam kontrolu nad situacijom",
        "Da pomognem drugima i podržim ih",
        "Da se zabavim i uživam u društvu",
      ],
      scores: [2, 2, 2, 0, 1],
    },
    {
      type: "radio",
      question: "Kako reaguješ na kritiku?",
      options: [
        "Prihvatam je i trudim se da učim iz nje",
        "Povremeno me pogađa, ali mogu da je podnesem",
        "Često me frustrira, ali pokušavam da je ignorišem",
        "Loše podnosim kritiku i pokušavam da je odbacim",
        "Kritika me ljuti i smatram je neosnovanom",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često osećaš da si posebna osoba u odnosu na druge?",
      options: [
        "Nikada",
        "Ponekad",
        "Često",
        "Skoro uvek",
        "Uveren/a sam da sam jedinstven/a i bolji/a od drugih",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako reaguješ kada ne dobiješ pažnju koju želiš? (označi sve što važi)",
      options: [
        "Osećam se povređeno",
        "Pokušavam da se istaknem još više",
        "Ignorišem situaciju i idem dalje",
        "Postajem frustriran/a i ljut/a",
        "Povlačim se i analiziram zašto nisam u centru pažnje",
      ],
      scores: [1, 2, 0, 2, 1],
    },
    {
      type: "radio",
      question: "Kako opisuješ svoje odnose sa drugima?",
      options: [
        "Puni empatije i razumevanja",
        "Uglavnom dobri, ali volim da budem dominantan/na",
        "Uživam u njima ako dobijam pažnju",
        "Osećam da su drugi tu da me podržavaju",
        "Ljudi su često ljubomorni na mene",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kada nešto želiš od drugih, kako to obično postižeš?",
      options: [
        "Objašnjavam i trudim se da ih ubedim argumentima",
        "Ponekad laskam ili koristim svoj šarm",
        "Često koristim emocionalni pritisak ili krivicu",
        "Uzimam ono što mi treba, bez obzira na druge",
        "Ljudi su tu da mi služe, zašto bih im objašnjavao/la?",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Kako reaguješ kada neko drugi dobije priznanje koje si želeo/la?",
      options: [
        "Radujem se njihovom uspehu",
        "Ponekad osećam zavist, ali ne pokazujem",
        "Frustriran/a sam, ali pokušavam da se smirim",
        "Osećam se nepravedno zapostavljenim/om",
        "Mislim da zaslužujem to više od njih",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko ti je važan status i moć?",
      options: [
        "Nije mi bitan",
        "Lepo je imati ih, ali nisu mi prioritet",
        "Prilično su mi važni",
        "Veoma su mi važni",
        "Neophodni su mi za život i sreću",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako se nosiš sa neuspehom? (označi sve što važi)",
      options: [
        "Analiziram šta mogu da popravim",
        "Krivim druge ili okolnosti",
        "Osećam se povređeno i poniženo",
        "Pokušavam da prikrijem neuspeh",
        "Brzo se oporavljam i nastavim dalje",
      ],
      scores: [0, 2, 2, 2, 1],
    },
    {
      type: "radio",
      question: "Kako reaguješ kada ti neko blizak ukaže na tvoju manu?",
      options: [
        "Zahvaljujem se i trudim da se poboljšam",
        "Ponekad me zaboli, ali razmislim o tome",
        "Branim se i nalazim izgovore",
        "Besan/na sam i smatram kritiku napadom",
        "Napadam ih nazad i nalazim njihove slabosti",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Opiši konkretnu situaciju u kojoj si primetio/la ovakvo ponašanje kod sebe:",
    },
  ],
  copy: {
    title: "Nivo tvoje narcisoidnosti",
    subtitle: "Razumevanje tvoje ličnosti",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
